import React, { Component } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import equipmentActions from "../../../../_actions/equipmentActions";
import ContractorSelector from "../_common/ContractorSelector";

const MODE = { STANDARD: "standard", MISC: "misc" };
const REQUIRED_COLUMNS = ["number", "class", "make", "model"];

function buildCatalogYearOptions(referenceDate) {
  const d = referenceDate ? new Date(referenceDate) : new Date();
  const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = fyStartYear - (5 - i);
    return {
      value: `${y}-04-01`,
      label: `${String(y).slice(2)}/${String(y + 1).slice(2)}`,
    };
  });
}

class EquipmentUploadModal extends Component {
  constructor(props) {
    super(props);
    const catalogYearOptions = buildCatalogYearOptions(null);
    this.state = {
      contractorId: 0,
      entryMode: MODE.STANDARD,
      selectedCatalogYear: catalogYearOptions[catalogYearOptions.length - 1],
      catalogYearOptions,
      file: null,
      fileName: "",
      parsedRows: [],
      parseErrors: [],
      isUploading: false,
      uploadError: null,
      uploadResult: null, // { successCount, rows }
    };
  }

  get reportDateStr() {
    return this.state.selectedCatalogYear
      ? this.state.selectedCatalogYear.value
      : new Date().toISOString().split("T")[0];
  }

  get equipmentType() {
    return this.state.entryMode === MODE.STANDARD ? "E" : "M";
  }

  handleChange_contractor = (contractorId) => this.setState({ contractorId });

  handleModeChange = (entryMode) => this.setState({ entryMode });

  handleCatalogYearChange = (selectedCatalogYear) => this.setState({ selectedCatalogYear });

  handleChange_file = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    this.setState(
      { file, fileName: file.name, parseErrors: [], parsedRows: [], uploadError: null, uploadResult: null },
      () => this.parseCSV(file)
    );
  };

  clearFile = () => {
    this.setState({
      file: null,
      fileName: "",
      parsedRows: [],
      parseErrors: [],
      uploadError: null,
      uploadResult: null,
    });
  };

  parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split(/\r?\n/).filter((line) => line.trim() !== "");

      if (lines.length < 2) {
        this.setState({
          parseErrors: ["CSV file must contain a header row and at least one data row."],
          parsedRows: [],
        });
        return;
      }

      const headers = this.parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
      const missingColumns = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
      if (missingColumns.length > 0) {
        this.setState({
          parseErrors: [
            `Missing required columns: ${missingColumns.join(", ")}. ` +
              `Required columns are: ${REQUIRED_COLUMNS.join(", ")}`,
          ],
          parsedRows: [],
        });
        return;
      }

      const rows = [];
      const errors = [];

      for (let i = 1; i < lines.length; i++) {
        const values = this.parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || "";
        });

        const rowErrors = [];
        if (!row.number) rowErrors.push("Number is required");
        if (!row.class) rowErrors.push("Class is required");
        if (!row.make) rowErrors.push("Make is required");
        if (!row.model) rowErrors.push("Model/Code is required");

        if (rowErrors.length > 0) {
          errors.push(`Row ${i}: ${rowErrors.join(", ")}`);
          continue;
        }

        rows.push({
          rowNumber: i,
          equipmentNumber: row.number,
          equipmentClass: row.class,
          equipmentMake: row.make,
          equipmentModel: row.model,
          isRental: row.isrental?.toLowerCase() === "true" || row.isrental === "1",
        });
      }

      this.setState({ parsedRows: rows, parseErrors: errors });
    };

    reader.readAsText(file);
  };

  canSubmit = () => {
    const { contractorId, parsedRows, parseErrors, isUploading } = this.state;
    return contractorId > 0 && parsedRows.length > 0 && parseErrors.length === 0 && !isUploading;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    if (!this.canSubmit()) return;

    const { contractorId, parsedRows } = this.state;
    const { projectId, dispatch } = this.props;

    this.setState({ isUploading: true, uploadError: null, uploadResult: null });

    try {
      const result = await dispatch(
        equipmentActions.uploadCsv({
          projectId,
          contractorId,
          equipmentType: this.equipmentType,
          reportDate: this.reportDateStr,
          rows: parsedRows,
        })
      );
      this.setState({ uploadResult: result, isUploading: false });
    } catch (err) {
      this.setState({
        uploadError: err.response?.data?.message || err.message || "Upload failed. Please try again.",
        isUploading: false,
      });
    }
  };

  render() {
    const {
      contractorId,
      entryMode,
      selectedCatalogYear,
      catalogYearOptions,
      fileName,
      parsedRows,
      parseErrors,
      isUploading,
      uploadError,
      uploadResult,
    } = this.state;
    const { showUploadModal, toggleUploadModal, projectId } = this.props;

    const failedRows = uploadResult?.rows?.filter((r) => !r.success) || [];

    const closeBtn = (
      <button className="close" onClick={toggleUploadModal}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );

    return (
      <Modal isOpen={showUploadModal} toggle={toggleUploadModal} size="lg" fade={false}>
        <ModalHeader toggle={toggleUploadModal} className="bg-info text-white" close={closeBtn}>
          <FontAwesomeIcon icon="upload" className="fa-fw mr-2" />
          Upload Equipment from CSV
        </ModalHeader>
        <ModalBody>
          <form id="uploadEquipForm" onSubmit={this.handleSubmit}>
            <ContractorSelector
              projectId={projectId}
              contractorId={contractorId}
              setContractorId={this.handleChange_contractor}
            />

            <div className="card mx-2 mt-2 bg-lightgray">
              <div className="card-body py-2">
                <div className="form-row align-items-center mb-2">
                  <label className="col-sm-3 col-form-label"><b>Rate Type:</b></label>
                  <div className="col-sm-9 my-auto">
                    <div className="btn-group btn-group-sm" role="group">
                      {[
                        { key: MODE.STANDARD, label: "Standard" },
                        { key: MODE.MISC, label: "Miscellaneous" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          className={`btn ${entryMode === key ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => this.handleModeChange(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="form-row align-items-center">
                  <label className="col-sm-3 col-form-label"><b>Rate Year:</b></label>
                  <div className="col-sm-4">
                    <Select
                      value={selectedCatalogYear}
                      options={catalogYearOptions}
                      onChange={this.handleCatalogYearChange}
                      styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                      isSearchable={false}
                    />
                  </div>
                  <div className="col-sm-5 my-auto">
                    <small className="text-muted">
                      Each row's Class/Make/Model is validated against this rate period.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="card m-2">
              <div className="card-body">
                <div className="form-group mb-3">
                  <label htmlFor="csvFile" className="font-weight-bold">
                    Select CSV File:
                  </label>
                  <div className="custom-file">
                    <input
                      type="file"
                      className="custom-file-input"
                      id="csvFile"
                      accept=".csv"
                      onChange={this.handleChange_file}
                      disabled={!contractorId}
                    />
                    <label className="custom-file-label" htmlFor="csvFile">
                      {fileName || "Choose file..."}
                    </label>
                  </div>
                  {fileName && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={this.clearFile}
                    >
                      <FontAwesomeIcon icon="times" className="mr-1" />
                      Clear
                    </button>
                  )}
                </div>

                <div className="alert alert-secondary mb-3">
                  <small>
                    <strong>Required CSV columns:</strong> Number, Class, Make, Model
                    <br />
                    <strong>Optional columns:</strong> IsRental
                    <br />
                    <strong>Note:</strong> Class/Make/Model must exactly match a catalog entry
                    valid for the selected catalog year and entry type — rate, description, and
                    OT/delay factors are pulled from the catalog automatically.
                  </small>
                </div>

                {parseErrors.length > 0 && (
                  <div className="alert alert-danger">
                    <strong>Validation Errors:</strong>
                    <ul className="mb-0 mt-2">
                      {parseErrors.slice(0, 15).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {parseErrors.length > 15 && (
                        <li>...and {parseErrors.length - 15} more errors</li>
                      )}
                    </ul>
                  </div>
                )}

                {uploadError && (
                  <div className="alert alert-danger">
                    <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                    {uploadError}
                  </div>
                )}

                {uploadResult && (
                  <div className={`alert ${failedRows.length > 0 ? "alert-warning" : "alert-success"}`}>
                    <FontAwesomeIcon
                      icon={failedRows.length > 0 ? "exclamation-triangle" : "check-circle"}
                      className="mr-2"
                    />
                    Uploaded <b>{uploadResult.successCount}</b> of{" "}
                    <b>{uploadResult.rows?.length || 0}</b> row(s) successfully.
                    {failedRows.length > 0 && (
                      <ul className="mb-0 mt-2">
                        {failedRows.map((r) => (
                          <li key={r.rowNumber}>
                            Row {r.rowNumber} ({r.equipmentNumber || "(no number)"}): {r.errorMessage}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {parsedRows.length > 0 && parseErrors.length === 0 && !uploadResult && (
                  <div>
                    <h6>Preview ({parsedRows.length} records):</h6>
                    <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <table className="table table-sm table-bordered">
                        <thead className="thead-light">
                          <tr>
                            <th>Number</th>
                            <th>Class</th>
                            <th>Make</th>
                            <th>Model</th>
                            <th>Rental</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedRows.slice(0, 20).map((row) => (
                            <tr key={row.rowNumber}>
                              <td>{row.equipmentNumber}</td>
                              <td>{row.equipmentClass}</td>
                              <td>{row.equipmentMake}</td>
                              <td>{row.equipmentModel}</td>
                              <td>{row.isRental ? "Yes" : "No"}</td>
                            </tr>
                          ))}
                          {parsedRows.length > 20 && (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                ...and {parsedRows.length - 20} more records
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <button
            type="submit"
            form="uploadEquipForm"
            className="btn btn-primary"
            disabled={!this.canSubmit()}
          >
            {isUploading ? (
              <React.Fragment>
                <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                Uploading...
              </React.Fragment>
            ) : (
              <React.Fragment>
                <FontAwesomeIcon icon="upload" className="mr-2" />
                Upload {parsedRows.length > 0 ? `(${parsedRows.length})` : ""}
              </React.Fragment>
            )}
          </button>
          <button type="button" className="btn btn-secondary ml-2" onClick={toggleUploadModal}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  projContractorList: state.contractors.projContractorLists[ownProps.projectId] || null,
});

export default connect(mapStateToProps)(EquipmentUploadModal);
